import { useRef } from 'react'
import { motion } from 'motion/react'
import { ProductCard } from '@/entities/product/ui'
import { useTelegram } from '@/shared/lib/telegram'
import { useAddToCart } from '@/features/cart/api/use-cart'
import { useNavigate } from '@tanstack/react-router'
import { useCartNotification } from '@/widgets/cart-notification'
import type { Teaware } from '@/entities/product'

export interface CatalogTeawareGridProps {
  /**
   * Список посуды для отображения
   */
  teaware: Teaware[]
  /**
   * Состояние загрузки
   */
  isLoading?: boolean
  /**
   * Количество skeleton карточек при загрузке
   * @default 6
   */
  skeletonCount?: number
}

/**
 * Виджет: Сетка посуды для каталога
 * 
 * Отображает посуду в сетку 2 колонки с карточками товаров.
 * Использует унифицированный ProductCard с встроенным FavoriteButton.
 */
export function CatalogTeawareGrid({
  teaware,
  isLoading = false,
  skeletonCount = 6,
}: CatalogTeawareGridProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const addToCart = useAddToCart()
  const cartNotification = useCartNotification()
  
  if (isLoading) {
    return (
      <div className="px-4 w-full">
        <div className="grid grid-cols-2 gap-4 gap-y-5 w-full">
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white dark:bg-dark-card rounded-[16px] overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.25)] border border-silk/20 dark:border-dark-border/20 w-full min-w-0"
            >
              {/* Премиальный skeleton с глянцевым эффектом */}
              <div className="relative aspect-square bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_1.5s_infinite] transform -skew-x-12" />
              </div>
              <div className="p-3 space-y-2.5">
                <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated rounded animate-pulse" />
                <div className="h-3 w-2/3 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated rounded animate-pulse" />
                <div className="h-5 w-1/2 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated rounded animate-pulse" />
                <div className="flex gap-2 pt-1">
                  <div className="flex-1 h-8 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-dark-elevated dark:via-dark-card dark:to-dark-elevated rounded-lg animate-pulse" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    )
  }
  
  if (teaware.length === 0) {
    return (
      <div className="px-4 py-16 text-center">
        <div className="mb-4">
          <svg
            className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <p className="text-lg font-medium text-ink dark:text-white mb-2">
          Ничего не найдено
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Попробуйте изменить запрос или сбросить фильтры
        </p>
      </div>
    )
  }
  
  const handleProductClick = (item: Teaware) => {
    navigate({
      to: '/product/$productId',
      params: { productId: item.id },
    })
  }
  
  const handleAddToCart = (item: Teaware) => {
    hapticFeedback.impactOccurred('medium')
    addToCart.mutate({
      productType: 'teaware',
      productId: item.id,
      quantity: 1,
    }, {
      onSuccess: (result) => {
        cartNotification.show({
          productName: item.name,
          productImage: item.images?.[0]?.url,
          cartItemId: result.data?.id,
        })
        hapticFeedback.notificationOccurred('success')
      },
    })
  }
  
  return (
    <div ref={containerRef} className="px-4 pb-6 w-full">
      <div className="grid grid-cols-2 gap-4 gap-y-5 w-full">
        {teaware.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: 0.2,
              delay: Math.min(index * 0.03, 0.3),
              ease: "easeOut"
            }}
            className="w-full min-w-0"
          >
            <ProductCard
              product={item}
              onClick={() => handleProductClick(item)}
              onAddToCart={() => handleAddToCart(item)}
              showAddToCart={true}
              showFavorite={true}
              onHapticFeedback={(type) => {
                hapticFeedback.impactOccurred(type || 'light')
              }}
              className="w-full"
            />
          </motion.div>
        ))}
      </div>
    </div>
  )
}
